// The agenda (prep) and, beside it, the playbooks rail (PlaybookRail) at a call
// and a practice run. Until 2026-10-31 the second column was Breeze (the prep
// chat, and the call's Live panel); it is off behind BREEZE_PANEL_ENABLED in
// lib/playbooks.ts, and turning that on brings it back as it was. On a wide
// screen they are two full-height columns, the prep toward the center and the
// second column on the right edge, at the width Breeze had, and each collapses
// to a thin rail that reopens it (remembered per column); with that column
// shut, the agenda takes its width. Narrower, they stack: the prep on top, the
// second pane below,
// and a divider between that drags like a code editor's, each pane collapsing
// to its header. "All preps" swaps the editor for the list in place, so a call
// can jump to another prep without leaving.

import { useCallback, useEffect, useRef, useState, type JSX, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import type { PrepListRow } from "@breeze/core";
import type { PrepEdit } from "../hooks/useAssistant";
import { ROOT_HANDLE } from "@breeze/core";
import { BREEZE_PANEL_ENABLED } from "../lib/playbooks";
import { listPreps } from "../lib/prepApi";
import { getKeyHandle } from "../lib/storage";
import { BackIcon, ChevronIcon, CloseIcon } from "./icons";
import { PlaybookRail } from "./PlaybookRail";
import { PrepChat } from "./PrepChat";
import { PrepEditor } from "./PrepEditor";

const SPLIT_KEY = "breeze:prep-split";
const PREP_SHUT_KEY = "breeze:prep-column-shut";
const BREEZE_SHUT_KEY = "breeze:breeze-column-shut";
const BOOKS_SHUT_KEY = "breeze:playbooks-column-shut";
const DEFAULT_SPLIT = 0.58;
const MIN_SPLIT = 0.15;
const MAX_SPLIT = 0.85;

function readSplit(): number {
  try {
    const v = Number(localStorage.getItem(SPLIT_KEY));
    if (Number.isFinite(v) && v >= MIN_SPLIT && v <= MAX_SPLIT) return v;
  } catch {
    // Storage is blocked: the default holds for this visit.
  }
  return DEFAULT_SPLIT;
}

function writeSplit(v: number | null): void {
  try {
    if (v === null) localStorage.removeItem(SPLIT_KEY);
    else localStorage.setItem(SPLIT_KEY, String(v));
  } catch {
    // Not remembered.
  }
}

/** Whether the Breeze column was left shut, for a screen that owns the button. */
export function readBreezeColumnShut(): boolean {
  return readShut(BREEZE_SHUT_KEY);
}

export function writeBreezeColumnShut(shut: boolean): void {
  writeShut(BREEZE_SHUT_KEY, shut);
}

export function readPrepColumnShut(): boolean {
  return readShut(PREP_SHUT_KEY);
}

export function writePrepColumnShut(shut: boolean): void {
  writeShut(PREP_SHUT_KEY, shut);
}

function readShut(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeShut(key: string, shut: boolean): void {
  try {
    if (shut) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch {
    // Not remembered.
  }
}

interface PrepWorkspaceProps {
  /** The prep to open first, if there is one. */
  readonly slug: string | null;
  /** The call's own Breeze panel; with it the bottom pane has Live and Breeze tabs. */
  readonly breezeLive?: ReactNode;
  /** The call's live text, handed to the chat. */
  readonly live?: () => string;
  /** Two full-height columns (prep, then Breeze) instead of the stacked panes. */
  readonly columns?: boolean;
  /** A prep Breeze changed on its own during the call (host only). */
  readonly edit?: PrepEdit | null;
  readonly onDismissEdit?: () => void;
  /** A screen with a Breeze button of its own owns whether Breeze is shut.
   * With these, the fold and the rail report through `onBreezeShutChange`. */
  readonly breezeShut?: boolean;
  readonly onBreezeShutChange?: (shut: boolean) => void;
  /** The same for the prep column, so a menu item can open and close it. */
  readonly prepShut?: boolean;
  readonly onPrepShutChange?: (shut: boolean) => void;
  /** The viewer is the call's host. The playbooks rail shows only for the
   * host signed in as the operator; anyone else gets the agenda alone. */
  readonly host?: boolean;
}

/** The operator's own playbooks, never a guest's view: the host, signed in
 * on this browser as the root account. The Worker checks the same. */
function mayShowPlaybooks(host: boolean): boolean {
  return host && getKeyHandle()?.toLowerCase() === ROOT_HANDLE;
}

type BottomTab = "live" | "chat";

export function PrepWorkspace({
  slug,
  breezeLive,
  live,
  columns = false,
  edit = null,
  onDismissEdit,
  breezeShut: breezeShutProp,
  onBreezeShutChange,
  prepShut: prepShutProp,
  onPrepShutChange,
  host = false,
}: PrepWorkspaceProps): JSX.Element {
  const [open, setOpen] = useState<string | null>(slug);
  const [view, setView] = useState<"prep" | "list">(slug ? "prep" : "list");
  const [refreshKey, setRefreshKey] = useState(0);
  const [listKey, setListKey] = useState(0);
  const [split, setSplit] = useState(readSplit);
  const [topShut, setTopShut] = useState(false);
  const [bottomShut, setBottomShut] = useState(false);
  const [prepColShut, setPrepColShut] = useState(() => readShut(PREP_SHUT_KEY));
  const [breezeColShut, setBreezeColShut] = useState(() => readShut(BREEZE_SHUT_KEY));
  const [booksColShut, setBooksColShut] = useState(() => readShut(BOOKS_SHUT_KEY));
  const showBooks = !BREEZE_PANEL_ENABLED && mayShowPlaybooks(host);
  const hasSide = BREEZE_PANEL_ENABLED || showBooks;
  const [tab, setTab] = useState<BottomTab>(breezeLive ? "live" : "chat");
  const [dragging, setDragging] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const splitRef = useRef(split);
  splitRef.current = split;

  // The call's prep can arrive after the workspace mounts.
  useEffect(() => {
    if (slug && slug !== open) {
      setOpen(slug);
      setView("prep");
    }
    // Only a new call prep moves the workspace; a prep opened from the list stays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (!breezeLive && tab === "live") setTab("chat");
  }, [breezeLive, tab]);

  const prepControlled = useRef(prepShutProp !== undefined);
  prepControlled.current = prepShutProp !== undefined;
  const openPrep = useCallback((next: string): void => {
    setOpen(next);
    setView("prep");
    setTopShut(false);
    if (prepControlled.current) onPrepShutChange?.(false);
    else {
      setPrepColShut(false);
      writeShut(PREP_SHUT_KEY, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Breeze edited or made a prep on its own: reload whatever is showing it.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    if (!edit) return;
    if (openRef.current && edit.changed.includes(openRef.current)) setRefreshKey((n) => n + 1);
    setListKey((n) => n + 1);
  }, [edit?.seq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Where "Open" goes: the prep it made, else the first one it changed that is not already open.
  const editTarget = edit ? (edit.created?.slug ?? edit.changed.find((s) => s !== open || view !== "prep") ?? null) : null;

  const onDividerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    const root = rootRef.current;
    if (!root) return;
    e.preventDefault();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    setDragging(true);
    setTopShut(false);
    setBottomShut(false);
    const move = (ev: PointerEvent): void => {
      const r = root.getBoundingClientRect();
      const v = (ev.clientY - r.top) / r.height;
      setSplit(Math.max(MIN_SPLIT, Math.min(MAX_SPLIT, v)));
    };
    const up = (): void => {
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      setDragging(false);
      writeSplit(splitRef.current);
    };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  }, []);

  const onDividerKey = useCallback((e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const step = e.shiftKey ? 0.1 : 0.03;
    let next: number | null = null;
    if (e.key === "ArrowUp") next = splitRef.current - step;
    else if (e.key === "ArrowDown") next = splitRef.current + step;
    else if (e.key === "Home") next = MIN_SPLIT;
    else if (e.key === "End") next = MAX_SPLIT;
    if (next === null) return;
    e.preventDefault();
    const v = Math.max(MIN_SPLIT, Math.min(MAX_SPLIT, next));
    setSplit(v);
    writeSplit(v);
  }, []);

  const resetSplit = useCallback((): void => {
    setSplit(DEFAULT_SPLIT);
    writeSplit(null);
    setTopShut(false);
    setBottomShut(false);
  }, []);

  const togglePrep = (): void => {
    if (prepShutProp !== undefined) {
      onPrepShutChange?.(!prepShutProp);
    } else if (columns) {
      const next = !prepColShut;
      setPrepColShut(next);
      writeShut(PREP_SHUT_KEY, next);
    } else setTopShut((v) => !v);
  };
  // A screen's Breeze button owns the Breeze column; the playbooks column
  // keeps its own shut state, so a stored "Breeze shut" never hides it.
  const sideControlled = BREEZE_PANEL_ENABLED && breezeShutProp !== undefined;
  const toggleBreeze = (): void => {
    if (sideControlled) {
      onBreezeShutChange?.(!breezeShutProp);
    } else if (columns && BREEZE_PANEL_ENABLED) {
      const next = !breezeColShut;
      setBreezeColShut(next);
      writeShut(BREEZE_SHUT_KEY, next);
    } else if (columns) {
      const next = !booksColShut;
      setBooksColShut(next);
      writeShut(BOOKS_SHUT_KEY, next);
    } else setBottomShut((v) => !v);
  };
  const prepShut = prepShutProp ?? (columns ? prepColShut : topShut);
  const breezeShut = sideControlled ? !!breezeShutProp : columns ? (BREEZE_PANEL_ENABLED ? breezeColShut : booksColShut) : bottomShut;

  const bottomIsShut = sideControlled ? !!breezeShutProp : bottomShut;
  const topFlex = !hasSide ? "1 1 0" : topShut ? "0 0 auto" : bottomIsShut ? "1 1 0" : `${split} 1 0`;
  const bottomFlex = bottomIsShut ? "0 0 auto" : topShut ? "1 1 0" : `${1 - split} 1 0`;

  return (
    <div
      className={`pw${columns ? " pw--cols" : ""}${columns && hasSide && breezeShut && !prepShut ? " pw--side-shut" : ""}${columns && !hasSide ? " pw--side-none" : ""}${dragging ? " pw--dragging" : ""}`}
      ref={rootRef}
    >
      <section
        className={`pw__pane${columns ? " pw__pane--prep" : ""}${prepShut ? (columns ? " pw__pane--rail" : " pw__pane--shut") : ""}`}
        style={columns ? undefined : { flex: topFlex }}
        aria-label="Agenda"
      >
        {columns && prepShut ? (
          <button type="button" className="pw__rail" aria-label="Show agenda" aria-expanded={false} onClick={togglePrep}>
            <ChevronIcon size={18} />
            <span>Agenda</span>
          </button>
        ) : null}
        <header className="pw__bar" hidden={columns && prepShut}>
          {view === "prep" ? (
            <button type="button" className="pw__back" onClick={() => setView("list")}>
              <BackIcon size={18} />
              <span>All agendas</span>
            </button>
          ) : (
            <>
              {open ? (
                <button type="button" className="pw__back" onClick={() => setView("prep")}>
                  <BackIcon size={18} />
                  <span>Back</span>
                </button>
              ) : (
                <span className="pw__label">All agendas</span>
              )}
            </>
          )}
          <button
            type="button"
            className={prepShut ? "pw__fold pw__fold--shut" : "pw__fold"}
            aria-label={prepShut ? "Show agenda" : "Hide agenda"}
            aria-expanded={!prepShut}
            onClick={togglePrep}
          >
            <ChevronIcon size={18} />
          </button>
        </header>
        {edit && !(columns && prepShut) ? (
          <div className="pw__edit" role="status">
            <p>{edit.text}</p>
            {editTarget ? (
              <button
                type="button"
                className="pw__editopen"
                onClick={() => {
                  openPrep(editTarget);
                  onDismissEdit?.();
                }}
              >
                Open
              </button>
            ) : null}
            <button type="button" className="pw__editx" aria-label="Dismiss" onClick={() => onDismissEdit?.()}>
              <CloseIcon size={16} />
            </button>
          </div>
        ) : null}
        <div className="pw__body" hidden={prepShut}>
          {view === "list" || !open ? <PrepList key={listKey} onOpen={openPrep} current={open} /> : <PrepEditor key={open} slug={open} refreshKey={refreshKey} />}
        </div>
      </section>

      {columns || !hasSide ? null : (
      <div
        className="pw__divider"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize panes"
        aria-valuemin={Math.round(MIN_SPLIT * 100)}
        aria-valuemax={Math.round(MAX_SPLIT * 100)}
        aria-valuenow={Math.round(split * 100)}
        tabIndex={0}
        onPointerDown={onDividerDown}
        onDoubleClick={resetSplit}
        onKeyDown={onDividerKey}
      >
        <span className="pw__grab" aria-hidden="true" />
      </div>
      )}

      {showBooks ? (
        <section
          className={`pw__pane${columns ? " pw__pane--side" : ""}${breezeShut ? (columns ? " pw__pane--rail" : " pw__pane--shut") : ""}`}
          style={columns ? undefined : { flex: bottomFlex }}
          aria-label="Playbooks"
        >
          {columns && breezeShut ? (
            <button type="button" className="pw__rail" aria-label="Show playbooks" aria-expanded={false} onClick={toggleBreeze}>
              <ChevronIcon size={18} />
              <span>Playbooks</span>
            </button>
          ) : (
            <PlaybookRail
              callSlug={slug}
              shut={breezeShut}
              fold={
                <button
                  type="button"
                  className={breezeShut ? "pw__fold pw__fold--shut" : "pw__fold"}
                  aria-label={breezeShut ? "Show playbooks" : "Hide playbooks"}
                  aria-expanded={!breezeShut}
                  onClick={toggleBreeze}
                >
                  <ChevronIcon size={18} />
                </button>
              }
            />
          )}
        </section>
      ) : null}

      {BREEZE_PANEL_ENABLED ? (
      <section
        className={`pw__pane${columns ? " pw__pane--side" : ""}${breezeShut ? (columns ? " pw__pane--rail" : " pw__pane--shut") : ""}`}
        style={columns ? undefined : { flex: bottomFlex }}
        aria-label="Breeze"
      >
        {columns && breezeShut ? (
          <button type="button" className="pw__rail" aria-label="Show Breeze" aria-expanded={false} onClick={toggleBreeze}>
            <ChevronIcon size={18} />
            <span>Breeze</span>
          </button>
        ) : null}
        <header className="pw__bar" hidden={columns && breezeShut}>
          {breezeLive ? (
            <div className="pw__tabs" role="tablist" aria-label="Breeze">
              <button type="button" role="tab" aria-selected={tab === "chat"} className={tab === "chat" ? "pw__tab pw__tab--on" : "pw__tab"} onClick={() => setTab("chat")}>
                Breeze
              </button>
              <button type="button" role="tab" aria-selected={tab === "live"} className={tab === "live" ? "pw__tab pw__tab--on" : "pw__tab"} onClick={() => setTab("live")}>
                Live
              </button>
            </div>
          ) : (
            <span className="pw__label">Breeze</span>
          )}
          <button
            type="button"
            className={breezeShut ? "pw__fold pw__fold--shut" : "pw__fold"}
            aria-label={breezeShut ? "Show Breeze" : "Hide Breeze"}
            aria-expanded={!breezeShut}
            onClick={toggleBreeze}
          >
            <ChevronIcon size={18} />
          </button>
        </header>
        <div className="pw__body" hidden={breezeShut}>
          {breezeLive ? (
            <div className="pw__live" hidden={tab !== "live"}>
              {breezeLive}
            </div>
          ) : null}
          <div className="pw__chat" hidden={breezeLive ? tab !== "chat" : false}>
            <PrepChat slug={open} live={live} onChanged={() => setRefreshKey((n) => n + 1)} onOpen={openPrep} />
          </div>
        </div>
      </section>
      ) : null}
    </div>
  );
}

// ---- the list ---------------------------------------------------------------------

function PrepList({ onOpen, current }: { readonly onOpen: (slug: string) => void; readonly current: string | null }): JSX.Element {
  const [rows, setRows] = useState<PrepListRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setFailed(false);
    const load = (first: boolean): void => {
      listPreps(controller.signal)
        .then((r) => setRows((now) => (now && JSON.stringify(now) === JSON.stringify(r) ? now : r)))
        .catch((err: unknown) => {
          if ((err as Error).name === "AbortError") return;
          if (first) setFailed(true);
        });
    };
    load(true);
    // New agendas and renames show up while the list is open, without a reload.
    const ask = (): void => {
      if (!document.hidden) load(false);
    };
    const timer = setInterval(ask, 3000);
    document.addEventListener("visibilitychange", ask);
    return () => {
      controller.abort();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", ask);
    };
  }, [tries]);

  if (failed) {
    return (
      <div className="pw__empty">
        <p>Could not load the agendas.</p>
        <button type="button" className="pe__retry" onClick={() => setTries((n) => n + 1)}>
          Try again
        </button>
      </div>
    );
  }
  if (!rows) return <div className="pw__empty">Loading</div>;
  if (rows.length === 0) return <div className="pw__empty">No agendas yet</div>;

  // An undated page of the same person and day as a dated one is the same meeting twice.
  const shown = rows.filter(
    (r) => r.startsAt || !rows.some((o) => o !== r && o.startsAt && o.title === r.title && r.when !== "" && o.when.startsWith(r.when)),
  );
  const upcoming = shown.filter((r) => !r.past);
  const undated = shown.filter((r) => r.past && !r.startsAt);
  const past = shown.filter((r) => r.past && r.startsAt);
  const row = (r: PrepListRow): JSX.Element => (
    <li key={r.slug}>
      <button type="button" className={r.slug === current ? "pw__row pw__row--on" : "pw__row"} onClick={() => onOpen(r.slug)}>
        <span className="pw__rowtitle">{r.title}</span>
        <span className="pw__rowwhen">{r.when}</span>
      </button>
    </li>
  );
  return (
    <div className="pw__list">
      {upcoming.length > 0 ? (
        <>
          <h2 className="pw__group">Upcoming</h2>
          <ul>{upcoming.map(row)}</ul>
        </>
      ) : null}
      {undated.length > 0 ? (
        <>
          <h2 className="pw__group">No date</h2>
          <ul>{undated.map(row)}</ul>
        </>
      ) : null}
      {past.length > 0 ? (
        <>
          <h2 className="pw__group">Past</h2>
          <ul>{past.map(row)}</ul>
        </>
      ) : null}
    </div>
  );
}
